import React, {MouseEvent, PureComponent} from "react"
import classNames from "classnames"
import PropTypes from "prop-types"
import styled from "styled-components"
import {omit, isFunction} from "lodash"

import History, {appOrContext} from "@irati/utils/history"
import {isPromise} from "@irati/utils/promise"
import id from "@irati/utils/id"

import StyledAnchor, {AnchorProps} from "./styled/Anchor"

export interface Props extends AnchorProps {
  to: string | object
  styled?: boolean
  scrollToTop?: boolean
  disabled?: boolean
  className?: string
  app?: string
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
  async?: boolean
  id?: string
  stopPropagation?: boolean
}

interface State {
  loading: boolean
}

class RouterLink extends PureComponent<Props, State> {
  static defaultProps = {
    styled: true,
    scrollToTop: true,
    stopPropagation: false
  }

  static contextTypes = {
    appPath: PropTypes.string
  }

  state: State = {
    loading: false
  }

  handleClick = (e) => {
    const {onClick, to, app, scrollToTop, stopPropagation} = this.props

    if (stopPropagation) {
      e.stopPropagation()
    }

    if (isFunction(onClick)) {
      onClick(e)
    }

    History.push(appOrContext({to, app}, this.context), "", {scrollToTop})
  }

  handleAsyncClick = () => {
    const {onClick, app, scrollToTop} = this.props

    if (isFunction(onClick)) {
      const result = onClick()
      if (isPromise(result)) {
        result
          .then((loc) => {
            this.setState({loading: false})

            if (!loc) return
            return History.push(appOrContext({to: loc, app}, this.context), "", {scrollToTop})
          })
          .catch((err) => {
            this.setState({loading: false})
            throw err
          })
        this.setState({loading: true})
      }
    }
  }

  onMouseDown = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault()
  }

  delegateClick = (e) => {
    e.preventDefault()

    const {disabled, async} = this.props
    const {loading} = this.state

    if (!loading && !disabled) {
      if (async) {
        this.handleAsyncClick(e)
      } else {
        this.handleClick(e)
      }
    }
  }

  render() {
    const {to, app, disabled, ...props} = this.props
    const {loading} = this.state
    const linkProps = omit(props, ["styled", "async", "onClick", "to"])
    linkProps.href = appOrContext({to, app}, this.context)
    if (linkProps.href && linkProps.href.pathname) {
      linkProps.href = linkProps.href.pathname
    }

    let Anchor = StyledAnchor
    if (!props.styled) {
      Anchor = styled.a``
    }

    const className = classNames(this.props.className, {
      disabled: disabled || loading,
      loading
    })

    return (
      <Anchor
        id={id("router-link", this.props)}
        {...linkProps}
        className={className}
        onMouseDown={this.onMouseDown}
        onClick={this.delegateClick}
      />
    )
  }
}

export default RouterLink
